import React, { Suspense, useEffect, useState } from 'react';
import { Redirect, Switch, useLocation, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
	isAuthenticated,
	hasContext,
	getUser,
	getClient,
	getAppliedGradepoints,
	getAppliedFilters,
	getAppliedComparisons,
	getAppliedExamLevels,
	getEmbargo,
	hasGroupAccess,
	getSubscriptionStatus,
	getViewAsSchool,
	getClientBenchmarkSet,
	getUserRoles,
	getCanSwitchClientBenchmark,
} from 'features/app/redux/context';

import { getFeature, getLoadingFlags } from 'features/app/redux/features';
import { getLoadingPermissions } from 'features/app/redux/permissions';
import { withinEmbargoAndViewAsSchool } from '../../utils/embargoHelpers';
import { isMATImpostor, isFullImpostor } from '../../utils/impersonation';
import { getCurrentSubscriptionAgreement } from 'features/manageAccount/redux';

const RouteWithSubRoutes = (route: Routes.Route) => {
	/**
	 ** Handle location
	 */
	const location = useLocation();

	/**
	 ** Authenticated flag
	 */
	const authenticated: boolean = useSelector(isAuthenticated);
	const contextLoaded: boolean = useSelector(hasContext);
	const user = useSelector(getUser);
	const client = useSelector(getClient);
	const selectedGp = useSelector(getAppliedGradepoints);
	const comparisons = useSelector(getAppliedComparisons);
	const filters = useSelector(getAppliedFilters);
	const examsLevels = useSelector(getAppliedExamLevels);
	const ff = useSelector(getFeature(route.permissions as keyof Features.Flags));
	const embargoFeature = useSelector(getFeature('embargo_feature'));
	const isLoadingFeatures = useSelector(getLoadingFlags);
	const isLoadingPermissions = useSelector(getLoadingPermissions);
	const applyEmbargo = useSelector(getEmbargo);
	const groupAccess = useSelector(hasGroupAccess);
	const [hasAccess, setHasAccess] = useState<boolean>(!!route.permissions && ff);
	const [returnUrl, setReturnUrl] = useState<string | null>(null);
	const subscriptionStatus = useSelector(getSubscriptionStatus);
	const canViewAsSchool = useSelector(getViewAsSchool);

	const allCurrentSubscriptions = useSelector(getCurrentSubscriptionAgreement);

	const benchmarkSet = useSelector(getClientBenchmarkSet);

	const isAdmin = useSelector(getUserRoles)?.includes('Settings_Users', 0);
	const earlyAccess = useSelector(getUserRoles)?.includes('EarlyAccessPrivilege', 0);
	const connectAccess = useSelector(getUserRoles)?.includes('DataExchange_Manage', 0);
	const hasPerformanceKs4Measures = useSelector(getUserRoles)?.includes(
		'ConnectKS4PerformanceMeasures',
		0
	);
	const hasPerformanceKs5Measures = useSelector(getUserRoles)?.includes(
		'ConnectKS5PerformanceMeasures',
		0
	);
	const canSwitchClientBenchmark = useSelector(getCanSwitchClientBenchmark);

	const impersonation =
		user && (isMATImpostor(user.impersonationMode) || isFullImpostor(user.impersonationMode));

	const embargoRedirect = withinEmbargoAndViewAsSchool(
		embargoFeature,
		applyEmbargo,
		canViewAsSchool
	);

	useEffect(() => {
		setHasAccess(!!route.permissions && ff);
	}, [ff, isLoadingFeatures, isLoadingPermissions]);

	// Clear returnUrlRedirect flag only when user navigates away AND subscription data is loaded
	// This prevents premature route guard re-evaluation that could redirect users away
	useEffect(() => {
		const isReturnUrlRedirect = window.sessionStorage.getItem('returnUrlRedirect') === 'true';
		const storedReturnUrlPath = window.sessionStorage.getItem('returnUrlPath');

		if (isReturnUrlRedirect && storedReturnUrlPath) {
			// Don't clear the flag if we're still on an auth page - a redirect is likely pending
			const isOnAuthPage = location.pathname.startsWith('/auth/');
			if (isOnAuthPage) {
				return undefined;
			}

			// Only clear the flag when it's truly safe to do so:
			// 1. User has navigated to a page that doesn't require subscription checks (like /home), OR
			// 2. Subscription data shows user has a valid subscription (bypass no longer needed)
			const stillOnReturnUrl = location.pathname === storedReturnUrlPath;
			const onAllowedPage = location.pathname === '/home' || location.pathname.startsWith('/auth/');
			const hasValidSubscription =
				subscriptionStatus &&
				typeof subscriptionStatus.hasValidSubscriptionOrContract === 'boolean' &&
				subscriptionStatus.hasValidSubscriptionOrContract;

			// Clear flag only when:
			// - User has navigated away from return URL AND
			// - (They're on an allowed page OR they have a valid subscription)
			if (!stillOnReturnUrl && (onAllowedPage || hasValidSubscription)) {
				window.sessionStorage.removeItem('returnUrlRedirect');
				window.sessionStorage.removeItem('returnUrlPath');
			}
		}
		return undefined;
	}, [location.pathname, subscriptionStatus]);

	// Default to private unless explicitly set
	route = {
		...route,
		private: route.private === undefined ? true : route.private,
	};

	useEffect(() => {
		// set the return url if we've tried to land somewhere that isn't a /auth route
		const { pathname = '' } = window.location;
		if (pathname && pathname.indexOf('/auth/') !== 0) {
			setReturnUrl(pathname);
		}
	}, []);

	const activeSubscription = allCurrentSubscriptions?.filter(
		(x) => x.subscriptionStatus == 'Active'
	)[0];

	useEffect(() => {
		if (
			authenticated &&
			window.dataLayer instanceof Array &&
			activeSubscription !== undefined &&
			groupAccess !== undefined
		) {
			window.dataLayer.push({
				event: 'login',
				userId: user?.id, // Current user's account ID
				name: user?.fullname, // current user's first name
				email: user?.username, // Current user's email
				organisationName: client?.name, // organisation name
				jobTitle: user?.jobTitle, // users job title
				country: client?.country?.name,
				currentSubscriptionType: activeSubscription?.subscriptionLevel,
				subscriptionStart: activeSubscription?.start,
				subscriptionEnd: activeSubscription?.end,
				zRef: client?.reference,
				accountConfig: benchmarkSet,
				adminUser: isAdmin,
				earlyAccess: earlyAccess,
				connectAccess: connectAccess,
				performanceMeasures: hasPerformanceKs4Measures || hasPerformanceKs5Measures,
				clientBenchmarks: canSwitchClientBenchmark,
				//CST want this to be false if groupAccess is null rather than null, I am casting here rather than unpicking the redux state
				isSummit: groupAccess === null ? false : groupAccess,
			});
		}
	}, [user, authenticated, client, activeSubscription, groupAccess]);

	const routesToBlock = [
		'Subjects',
		'Student Analysis',
		'Subject Analysis',
		'KS4 Performance Measures',
		'KS5 Performance Measures (A-levels)',
		'International Baccalaureate Performance Measures',
		'Strategic',
		'Monitoring',
		'Monitoring Accuracy',
		!groupAccess ? 'Reports' : '',
		//Summit?
		'Subject Overview',
		'BBPA Analysis',
		'Strategic Analysis',
	];

	const allowedRoutesForInValidSubscriptionOrContract = [
		'Home',
		'Manage Users',
		'Manage Account',
		'View As School/College',
	];
	return (
		<Suspense fallback={route.fallback}>
			<Route
				path={route.path}
				render={(props) => {
					/**
					 ** Handle redirects
					 */
					if (route.redirect) {
						return (
							<Switch>
								<Redirect
									to={{
										pathname: route.redirect,
										state: { referrer: location.pathname },
									}}
								/>
							</Switch>
						);
					} // && withinKs4Embargo || withinKs5Embargo

					/**
					 * Check if this is a returnUrl redirect (user was on this page before 401)
					 * This flag allows bypassing embargo and subscription checks
					 */
					const isReturnUrlRedirect = window.sessionStorage.getItem('returnUrlRedirect') === 'true';

					/**
					 * Handle redirection for embargo period
					 * Skip this check if this is a returnUrl redirect (user was on this page before 401)
					 */
					if (
						!isReturnUrlRedirect &&
						embargoFeature &&
						route.baseName &&
						routesToBlock.includes(route.baseName) &&
						embargoRedirect
					) {
						return <Redirect to={'/home'} />;
					}

					/**
					 ** Handle private routes
					 */
					if (route.private) {
						/*
							Don't do anything if we're still loading the connectctx call, otherwise
							we'll fall into the "not authenticated" route and redirect to the home page
							causing deep links not to work
						 */
						if (!contextLoaded || isLoadingFeatures || isLoadingPermissions) {
							return null;
						}

						/**
						 * If the user is  not authenticated send them to the login screen
						 */
						if (!authenticated) {
							return <Redirect to={`/auth/login${returnUrl ? `?ReturnUrl=${returnUrl}` : ''}`} />;
						}

						/**
						 * Handle redirection for subscription
						 * Skip this check if this is a returnUrl redirect (user was on this page before 401)
						 */
						const subscriptionCheck =
							!isReturnUrlRedirect &&
							!subscriptionStatus.hasValidSubscriptionOrContract &&
							route.baseName &&
							!allowedRoutesForInValidSubscriptionOrContract.includes(route.baseName) &&
							!impersonation &&
							user !== undefined &&
							window.sessionStorage.getItem('isImpersonation') !== 'true';

						if (subscriptionCheck) {
							return <Redirect to={'/home'} />;
						}

						/**
						 * If we have a route component and
						 * a/ the route does not require permissions
						 * b/ the route requires permissions and the user has access
						 */
						if ((route.component && !route.permissions) || (route.component && hasAccess)) {
							return (
								<route.component {...props} display={route.displayName} routes={route.routes} />
							);
						}

						/**
						 * If the user does not have access to the feature
						 */
						return (
							<Redirect
								to={{ pathname: '/page-not-found', state: { referrer: location.pathname } }}
							/>
						);
					}

					/**
					 ** Handle standard routes
					 */
					return (
						route.component && (
							<route.component {...props} display={route.displayName} routes={route.routes} />
						)
					);
				}}
			/>
		</Suspense>
	);
};

export default RouteWithSubRoutes;
